import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Form, InputNumber, Modal, Select, Table, Tag, message } from 'antd';
import { ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import client from '../../api/client';
import './style.css';

export default function AiSafety() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [outcome, setOutcome] = useState('REVIEWED');
  const [ticketId, setTicketId] = useState(null);
  const load = useCallback(async (before) => {
    setBusy(true);
    try { const result = await client.get('/ai-safety', { params: before ? { before } : {} }); setRows(result || []); }
    catch { message.error('读取审核记录失败'); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    setBusy(true);
    try {
      await client.post(`/ai-safety/${selected.id}/reviews`, { outcome, ticketId });
      setSelected(null); await load(); message.success('复核记录已追加');
    } catch { message.error('保存失败，请检查工单和权限'); }
    finally { setBusy(false); }
  };
  const columns = [
    { title: '事件编号', dataIndex: 'id', width: 90 },
    { title: '内容 / 请求编号', dataIndex: 'request_id', width: 280, ellipsis: true, className: 'ai-safety-id' },
    { title: '阶段', dataIndex: 'phase', width: 110, render: value => ({ INPUT: '输入检查', OUTPUT: '输出检查', RESULT: '结果交付', REVIEW: '人工复核' }[value] || value) },
    { title: '检查 / 复核结果', dataIndex: 'decision', width: 195, ellipsis: true, render: value => <Tag color={value === 'PASS' || value === 'DELIVERED' ? undefined : value === 'REVIEWED' ? 'blue' : 'orange'}>{({ REVIEWED: '已人工核查', CONFIRMED_RISK: '确认存在风险', FALSE_POSITIVE: '确认规则误拦截', CORRECTED: '已纠正并反馈', DISABLED: '已停用功能', PASS: '未命中规则', DELIVERED: '已交付', PROMPT_OVERRIDE: '提示词越权拦截' }[value] || value)}</Tag> },
    
    { title: '时间', dataIndex: 'created_at', width: 190 },
    { title: '复核人 ID', dataIndex: 'actor_id', width: 100, render: value => value ?? '—' },
    { title: '工单编号', dataIndex: 'ticket_id', width: 100, render: value => value ?? '—' },
    { title: '操作', width: 125, fixed: 'right', render: (_, row) => row.phase !== 'REVIEW' ? <Button onClick={() => { setSelected(row); setOutcome('REVIEWED'); setTicketId(null); }}>追加复核</Button> : '已记录' },
  ];
  return <div className="ai-safety-page">
    <div className="ai-safety-heading">
      <div><h2><SafetyCertificateOutlined /> AI审核记录</h2><p>查看检查结果、追溯生成内容与追加人工复核。</p></div>
      <Button icon={<ReloadOutlined />} onClick={() => load()} loading={busy}>刷新最新</Button>
    </div>
    <div className="ai-safety-stats">
      {[['本批事件', rows.length], ['输入检查', rows.filter(row => row.phase === 'INPUT').length], ['输出检查', rows.filter(row => row.phase === 'OUTPUT').length], ['人工复核', rows.filter(row => row.phase === 'REVIEW').length]].map(([label, count]) =>
        <div className="ai-safety-stat" key={label}><span>{label}</span><strong>{count}</strong></div>)}
    </div>
    <Alert type="info" showIcon message="PASS仅表示未命中当前版本规则，不代表内容已全面审核通过。此处不保存对话正文；需结合用户反馈工单核实后追加复核。" />
    <div className="ai-safety-toolbar"><span>事件明细 <small>点击左侧展开查看来源及完整摘要</small></span>
      <Button disabled={busy || rows.length < 100} onClick={() => load(rows[rows.length - 1]?.id)}>更早记录</Button></div>
    <Table rowKey="id" columns={columns} dataSource={rows} loading={busy} pagination={false}
      tableLayout="fixed" scroll={{ x: 1340 }} size="middle"
      expandable={{ expandedRowRender: row => <Descriptions size="small" column={1} style={{ overflowWrap: 'anywhere' }} items={[
        { key: 'request', label: '完整内容 / 请求编号', children: row.request_id },
        { key: 'rule', label: '规则版本', children: row.rule_version || '—' },
        { key: 'model', label: '模型配置', children: row.model_name || '—' },
        { key: 'source', label: '结果来源', children: ({ MODEL_ASSISTED: '采用模型建议（含规则后处理）', RULE_FALLBACK: '规则降级' }[row.generation_source] || (row.phase === 'REVIEW' ? '复核事件不产生模型结果' : '该事件未记录')) },
        { key: 'hash', label: '内容摘要 SHA256', children: row.content_sha256 || '该事件未记录' },
      ]} /> }} />
    <Modal width={560} okText="保存复核记录" cancelText="取消" title="追加人工复核记录" open={!!selected} onCancel={() => setSelected(null)} onOk={submit} confirmLoading={busy}>
      <p>请选择实际已完成的处理；此操作不会自动删除内容或关闭功能。</p>
      <p style={{ overflowWrap: 'anywhere' }}>内容 / 请求编号：{selected?.request_id}</p>
      <Form layout="vertical" style={{ marginTop: 24 }}>
        <Form.Item label="复核结论" required>
        <Select value={outcome} onChange={setOutcome} style={{ width: '100%' }} options={[
          { value: 'REVIEWED', label: '已人工核查' }, { value: 'CONFIRMED_RISK', label: '确认存在风险' },
          { value: 'FALSE_POSITIVE', label: '确认规则误拦截' }, { value: 'CORRECTED', label: '已完成纠正并反馈' },
          { value: 'DISABLED', label: '已另行停用功能' },
        ]} />
        </Form.Item>
        <Form.Item label="关联工单" extra="仅填写与本次复核有关的真实工单；没有可留空。">
        <InputNumber min={1} precision={0} placeholder="相关真实工单编号（选填）" value={ticketId} onChange={setTicketId} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  </div>;
}
